/* eslint-disable complexity */
import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSidebar } from '@adornis/chemistry/elements/components/x-sidebar.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser, impersonatingUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import '@chancenwerk/components/chancen-component-logout-button.js';
import '@chancenwerk/design/components/cr-sidebar-item.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import logoWhite from '../assets/logo_chancenwerk_white.svg';
import { ChromiumDialog } from './cr-dialog.js';

/**
 * @customElement cr-sidebar
 */
@customElement('cr-sidebar')
export class ChromiumSidebar extends XSidebar {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() private readonly _impersonating = new RXController(this, impersonatingUser<ChancenwerkUser>());

  @property({ attribute: false }) public version = '';
  @property({ attribute: false }) public labelOverride: Maybe<string> = null;
  @property({ attribute: false }) public hrefOverride: Maybe<string> = null;

  override styles() {
    return [
      {
        ':host': {
          width: this.isOpen.value ? (this.size.mobile ? '100%' : '256px') : '0px',
          height: '100%',
          position: this.size.mobile ? 'fixed' : 'static',
          transform: this.isOpen.value ? 'translateX(0)' : 'translateX(-256px)',
          display: 'flex',
          transition: 'all .3s ease-in-out',
          zIndex: '0',
          background: this.colors.primary,
        },
        'cr-button': {
          color: 'white',
        },
      } satisfies Styles,
    ];
  }

  // TODO workaround for engine issue -> delete this shit
  override connectedCallback(): void {
    super.connectedCallback();
    setTimeout(() => {
      if (this.size.mobile) ChromiumSidebar.close();
    }, 100);
  }

  override render() {
    return html`
      <cr-button
        mode="fab"
        icon="menu"
        ${css({
          transform: this.isOpen.value ? 'translateX(-5rem)' : 'translateX(256px)',
          transitionDelay: !this.isOpen.value ? '0.15s' : '0',
          opacity: this.isOpen.value ? '0' : '1',
          position: 'absolute',
          zIndex: '50',
          bottom: this.spacing.md,
          left: this.spacing.md,
          color: 'white',
          backgroundColor: this.colors.primary,
          backgroundImage: `linear-gradient(to left, ${this.colors.primary} 0%, ${this.colors.primary} 25%, ${this.colors.primary} 75%, ${this.colors.primary} 100%)`,
        })}
        @click=${() => ChromiumSidebar.open()}
      ></cr-button>

      <cr-flex w100 space="md" ${css({ zIndex: '1000' })}>
        ${this.size.mobile ? html`` : nothing} ${!this.size.mobile ? this._logo() : nothing}
        <cr-flex ${css({ overflowX: 'hidden' })} flex space-between>
          ${this.size.mobile ? this._logo() : nothing}
          ${isImpersonating.value
            ? html`
                <cr-card padding="md" margin="xs" space="sm">
                  <cr-flex space="sm" horizontal center crossaxis-center>
                    <cr-text bold ${css({ color: this.colors.tone.error })}> Impersonation aktiv </cr-text>
                    <cr-text ${css({ color: this.colors.tone.error })}>
                      <cr-icon>
                        info

                        <cr-tooltip>
                          <cr-flex space="md" ${css({ maxWidth: '500px' })}>
                            <cr-flex space="xs">
                              <cr-text bold>Ich bin gerade</cr-text>
                              <cr-text>${this._currentUser.value?.toString()}</cr-text>
                            </cr-flex>
                            <cr-flex space="xs">
                              <cr-text bold>Ich bin eigentlich</cr-text>
                              <cr-text>${this._impersonating.value?.toString()}</cr-text>
                            </cr-flex>
                            <cr-text>
                              Impersonation erlaubt es dir die Rechte anderer Nutzer auszuprobieren. Die Funktion ist
                              nur zu Testzwecken gedacht und in der Liveumgebung deaktiviert. Dieser Status gilt nur im
                              CHANCENraum. Auf den anderen Plattformen bist du immer noch du selbst.
                            </cr-text>
                          </cr-flex>
                        </cr-tooltip>
                      </cr-icon>
                    </cr-text>
                  </cr-flex>

                  <cr-button
                    mode="outline"
                    icon="cancel"
                    tone="error"
                    @click=${async () => {
                      await ChancenwerkUser.stopImpersonation();
                    }}
                  >
                    Beenden
                  </cr-button>
                </cr-card>
              `
            : nothing}

          <cr-flex h100 ${css({ paddingBottom: this.spacing.md })}>
            <slot></slot>
          </cr-flex>

          <cr-flex space="sm" crossaxis-center flex>
            <slot name="bottom-upper"></slot>
          </cr-flex>
        </cr-flex>
        ${this._getStickyFooter()}
      </cr-flex>
    `;
  }

  private _getVersionString() {
    return html`
      <cr-text
        ${css({ color: 'white', fontSize: '0.8rem' })}
        @dblclick=${async () => {
          const userData = this._currentUser.value?.toJSON();
          const sessionID = localStorage.getItem('sessionID');
          const cookies = document.cookie;

          const data = btoa(
            JSON.stringify(
              {
                userData,
                sessionID,
                cookies,
              },
              null,
              2,
            ),
          );

          console.log('DEBUG', {
            userData,
            sessionID,
            cookies,
          });

          ChromiumDialog.showPopup({
            content: resolve => {
              return html`<cr-flex ${css({ width: '500px' })} padding="lg" space="lg">
                <cr-text>
                  Hier kannst du Daten herunterladen die bei der Fehlerbehung helfen können. Teile diese bitte nur mit
                  Entwicklern von Adornis.
                </cr-text>
                <cr-button
                  @click=${() => download(data, `${userData?._id ?? 'no_logon'}_${new Date().toISOString()}.debug`)}
                >
                  Download Debug Data
                </cr-button>
                <cr-hr></cr-hr>
                <cr-button @click=${resolve}>Schließen</cr-button>
              </cr-flex>`;
            },
          });
        }}
        >Version: ${this.version}
      </cr-text>
    `;
  }

  private _getStickyFooter() {
    return html`
      <cr-flex space="xs" center crossaxis-center ${css({ paddingBottom: this.spacing.sm })}>
        <cr-text small ${css({ color: 'white' })}>${this._currentUser.value?.toString()}</cr-text>
        <cr-flex horizontal space="sm" center w100 crossaxis-center padding="sm">
          <chancen-component-logout-button
            .labelOverride=${this.labelOverride}
            .hrefOverride=${this.hrefOverride}
          ></chancen-component-logout-button>
          <slot name="footer-buttons"></slot>
          <cr-flex
            center
            crossaxis-center
            ${css({
              flex: 1,
              color: 'white',
              border: '1px solid white',
              cursor: 'pointer',
              borderRadius: this.sizes.borderRadius,
            })}
            @click=${() => ChromiumSidebar.toggle()}
            padding="sm"
          >
            <cr-icon>${ChromiumSidebar.isOpen() ? 'chevron_left' : 'chevron_right'}</cr-icon>
            <cr-tooltip>
              <cr-text> Navigation einklappen </cr-text>
            </cr-tooltip>
          </cr-flex>
        </cr-flex>
        <cr-text ${css({ color: 'white' })}> Ein Produkt von </cr-text>
        <cr-image
          disabled
          loading="lazy"
          mode="width"
          .value=${logoWhite}
          ${css({ width: '70%', cursor: 'pointer' })}
          @click=${() => goTo('https://chancenwerk.de/', { target: '_blank' })}
        ></cr-image>
        <slot name="footer"></slot>
        ${this.version ? this._getVersionString() : nothing}
      </cr-flex>
    `;
  }
}
